import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CloudUpload, Loader2, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { FullPageLoader } from "@/components/ProtectedRoute";
import { JourneyMap } from "@/components/assessment/JourneyMap";
import { BreakScreen, SummitScreen, WelcomeScreen } from "@/components/assessment/JourneyScreens";
import { STOPS, STOP_SIZE as SIZE, TOTAL, factsFor } from "@/components/assessment/journey";

const welcomeKey = (id) => `disha-welcome-${id}`;

export default function Assessment() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [screen, setScreen] = useState("question");
  const [breakStop, setBreakStop] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const facts = useMemo(() => factsFor(sessionId), [sessionId]);

  useEffect(() => {
    api.get(`/sessions/${sessionId}`).then(({ data: d }) => {
      if (d.status !== "in_progress") return navigate("/dashboard", { replace: true });
      setData(d);
      const open = d.questions.findIndex((q) => q.answer == null);
      if (open === -1) { setIdx(TOTAL - 1); return setScreen("summit"); }
      setIdx(open);
      if (open === 0 && !localStorage.getItem(welcomeKey(sessionId))) setScreen("welcome");
      else if (open % SIZE === 0 && open > 0 && d.module === open / SIZE - 1) { setBreakStop(open / SIZE - 1); setScreen("break"); }
    }).catch((e) => { toast.error(errMsg(e)); navigate("/dashboard"); });
  }, [sessionId, navigate]);

  const answered = useMemo(() => data?.questions.filter((q) => q.answer != null).length ?? 0, [data]);
  if (!data) return <FullPageLoader />;
  const q = data.questions[idx];
  const stop = Math.floor(idx / SIZE);
  const inStop = idx % SIZE;
  const stopQs = data.questions.slice(stop * SIZE, stop * SIZE + SIZE);
  const stopAnswered = stopQs.filter((x) => x.answer != null).length;

  const pause = () => { toast.success("Progress saved. You can resume from the same question anytime."); navigate("/dashboard"); };
  const finishStop = (qs) => {
    if (stop < 4) { setBreakStop(stop); setScreen("break"); } else if (qs.every((x) => x.answer != null)) setScreen("summit");
  };
  const go = (i) => { setIdx(i); setScreen("question"); };

  const choose = async (option) => {
    setSaving(true);
    try {
      const { data: r } = await api.post(`/sessions/${sessionId}/answer`, { index: idx, option });
      const qs = data.questions.map((x, i) => (i === idx ? { ...x, answer: option } : x));
      setData((d) => ({ ...d, module: r.module, questions: qs }));
      const stopDone = qs.slice(stop * SIZE, stop * SIZE + SIZE).every((x) => x.answer != null);
      if (inStop === SIZE - 1 && stopDone) setTimeout(() => finishStop(qs), 250);
      else if (inStop < SIZE - 1) setTimeout(() => setIdx(idx + 1), 180);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (confirmed = false) => {
    setSubmitting(true);
    try {
      const { data: r } = await api.post(`/sessions/${sessionId}/submit`, { confirm_straightline: confirmed });
      if (r.needs_confirmation) return setConfirm(r.straightline);
      toast.success("Submitted! Your report is being prepared.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  const body = {
    welcome: <WelcomeScreen name={data.student?.name} onStart={() => { localStorage.setItem(welcomeKey(sessionId), "1"); setScreen("question"); }} />,
    break: <BreakScreen stop={breakStop} fact={facts[breakStop]} onContinue={() => go((breakStop + 1) * SIZE)} onPause={pause} onReview={() => go(breakStop * SIZE)} />,
    summit: <SummitScreen submitting={submitting} onSubmit={() => submit(false)} onReview={() => go(4 * SIZE)} />,
  }[screen];

  return (
    <div className="max-w-2xl mx-auto" data-testid="assessment-page">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 text-ds-inksoft" data-testid="assessment-save-status">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5" />}{saving ? "Saving" : "All answers saved"}</span>
        <button onClick={pause} className="inline-flex items-center gap-1.5 rounded-full border border-ds-plum/20 bg-ds-cream min-h-[44px] px-4 text-sm font-semibold text-ds-plum hover:bg-ds-plum/5 transition-colors" data-testid="assessment-pause-button"><PauseCircle className="h-4 w-4" />Pause</button>
      </div>
      <div className="mt-4 h-1 rounded-full bg-ds-plum/10 overflow-hidden" role="progressbar" aria-valuenow={answered} aria-valuemin={0} aria-valuemax={TOTAL} data-testid="assessment-progress-bar">
        <div className="h-full rounded-full bg-ds-plum transition-[width] duration-500" style={{ width: `${answered}%` }} />
      </div>
      {body ? <div className="mt-5">{body}</div> : (
        <>
          <div className="mt-5"><JourneyMap completed={stop} current={stop} progress={stopAnswered / SIZE} /></div>
          <div className="mt-5 flex flex-wrap items-baseline justify-between gap-x-3">
            <p className="font-display text-lg font-semibold text-ds-plum" data-testid="assessment-progress">Question {inStop + 1} of {SIZE} at {STOPS[stop]}</p>
            <p className="text-xs text-ds-inksoft" data-testid="assessment-overall-progress">{answered} of {TOTAL} answered</p>
          </div>
          <div key={idx} className="mt-4 rounded-[24px] bg-white border border-ds-plum/10 p-6 sm:p-10 rise">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold leading-snug text-ds-plumdeep" data-testid="assessment-question-text">{q.text}</h1>
            <div className="mt-8 grid gap-3">
              {q.options.map((opt, i) => (
                <button key={opt} onClick={() => choose(i)} disabled={saving}
                  className={`min-h-[56px] rounded-2xl border px-5 text-left font-semibold transition-colors duration-200 flex items-center justify-between ${q.answer === i ? "bg-ds-plum border-ds-plum text-white" : "bg-ds-cream border-ds-plum/15 text-ds-ink hover:border-ds-plum hover:bg-ds-plum/5"}`}
                  data-testid={`likert-option-${q.id}-${i}`}>
                  {opt}{q.answer === i && <Check className="h-5 w-5" />}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 flex justify-between gap-3">
            <button onClick={() => setIdx(idx - 1)} disabled={inStop === 0} className="ds-btn border border-ds-plum/20 bg-ds-cream text-ds-plum disabled:opacity-40 disabled:hover:translate-y-0" data-testid="assessment-prev-button"><ArrowLeft className="h-4 w-4" />Back</button>
            {inStop === SIZE - 1
              ? <button onClick={() => finishStop(data.questions)} disabled={stopAnswered < SIZE} className="ds-btn bg-ds-plum text-white hover:bg-[#3d0232] disabled:opacity-40" data-testid="assessment-next-button">Finish {STOPS[stop]}<ArrowRight className="h-4 w-4" /></button>
              : <button onClick={() => setIdx(idx + 1)} disabled={q.answer == null} className="ds-btn bg-ds-plum text-white hover:bg-[#3d0232] disabled:opacity-40" data-testid="assessment-next-button">Next<ArrowRight className="h-4 w-4" /></button>}
          </div>
        </>
      )}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-white" data-testid="straightline-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Quick check before you submit</AlertDialogTitle>
            <AlertDialogDescription>The same answer was chosen for about {confirm?.max_share_pct}% of questions. If those answers truly reflect you, that's completely fine. Submit anyway?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="straightline-review-button">Review answers</AlertDialogCancel>
            <AlertDialogAction onClick={() => submit(true)} className="bg-ds-plum" data-testid="straightline-confirm-button">Submit anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
